import type { Emissary } from 'api-validations/emissary'

export type AccountDevice = Emissary.Account['devices'][number]

export const ROSTER_DEBOUNCE_MS = 5_000

enum RosterState {
  Unheard = 'unheard',
  Heard = 'heard'
}

enum ChangeKind {
  Joining = 'joining',
  Leaving = 'leaving'
}

type PendingChange =
  | { kind: ChangeKind.Joining; device: AccountDevice; since: number }
  | { kind: ChangeKind.Leaving; since: number }

export type LiveRoster =
  | { state: RosterState.Unheard }
  | {
      state: RosterState.Heard
      live: ReadonlyMap<number, AccountDevice>
      pending: ReadonlyMap<number, PendingChange>
    }

export const EMPTY_ROSTER: LiveRoster = { state: RosterState.Unheard }

export const isHeard = (roster: LiveRoster): boolean => roster.state === RosterState.Heard

export const liveDevices = (roster: LiveRoster): AccountDevice[] =>
  roster.state === RosterState.Unheard ? [] : Array.from(roster.live.values())

export const receiveAccount = ({
  roster,
  devices,
  now
}: {
  roster: LiveRoster
  devices: AccountDevice[]
  now: number
}): LiveRoster => {
  const received = new Map(devices.map((device) => [device.serialNumber, device]))

  if (roster.state === RosterState.Unheard) {
    return { state: RosterState.Heard, live: received, pending: new Map() }
  }

  const live = new Map(roster.live)
  const pending = new Map<number, PendingChange>()

  for (const [serial, device] of received) {
    if (live.has(serial)) {
      live.set(serial, device)
      continue
    }
    const previous = roster.pending.get(serial)
    const since = previous?.kind === ChangeKind.Joining ? previous.since : now
    pending.set(serial, { kind: ChangeKind.Joining, device, since })
  }

  for (const serial of roster.live.keys()) {
    if (received.has(serial)) continue
    const previous = roster.pending.get(serial)
    const since = previous?.kind === ChangeKind.Leaving ? previous.since : now
    pending.set(serial, { kind: ChangeKind.Leaving, since })
  }

  return { state: RosterState.Heard, live, pending }
}

export const settleRoster = ({ roster, now }: { roster: LiveRoster; now: number }): LiveRoster => {
  if (roster.state === RosterState.Unheard) return roster

  const live = new Map(roster.live)
  const pending = new Map<number, PendingChange>()

  for (const [serial, change] of roster.pending) {
    if (now - change.since < ROSTER_DEBOUNCE_MS) {
      pending.set(serial, change)
      continue
    }
    if (change.kind === ChangeKind.Joining) {
      live.set(serial, change.device)
    } else {
      live.delete(serial)
    }
  }

  return { state: RosterState.Heard, live, pending }
}

export const nextSettleAt = (roster: LiveRoster): number | undefined => {
  if (roster.state === RosterState.Unheard || roster.pending.size === 0) return undefined
  const earliest = Math.min(...Array.from(roster.pending.values(), (change) => change.since))
  return earliest + ROSTER_DEBOUNCE_MS
}
